import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import { VerticalConfig } from './atnaConfig';
import { useLanguage } from '../LanguageContext';
import { resolveAnswer, questionLabel, VerificationScore, VerificationLabel, WhyThis, InformActNote } from './verification';
type AtnaAssistantProps = {
  vertical: VerticalConfig;
  page?: string;
};
export function AtnaAssistant({
  vertical,
  page = 'unknown'
}: AtnaAssistantProps) {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [typed, setTyped] = useState('');
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {}, []);
  const pick = (q: string) => {
    setActiveQuestion(q);
    setExpanded(false);
    trackEvent('atna_question_clicked', {
      question: q,
      page,
      vertical: vertical.tag
    });
  };
  const reset = () => {
    setActiveQuestion(null);
    setExpanded(false);
    setTyped('');
    setShowHint(false);
  };
  const resolved = resolveAnswer(vertical, activeQuestion, isFR);
  return <section className="py-24 px-6 bg-white" id="atna" data-id="element-629" data-mp="w0q66hjridtf">
      <div className="max-w-3xl mx-auto" data-id="element-630" data-mp="w19q23sz3ca3">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-631" data-mp="8ads0oxxbkue">
          <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-632" data-mp="h9r3to9islia">
            {isFR ? 'Assistant ATNA' : 'ATNA Assistant'}
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-633" data-mp="1spfqsfmrxkj3">
            {isFR ? 'Une intelligence vérifiée pour les communautés.' : 'Verified intelligence for communities.'}
          </h2>
        </motion.div>

        {/* Panel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="border border-[#E4E6EC] bg-white" data-id="element-634" data-mp="qc9phfvasbfq">
          <div className="flex items-center justify-between px-6 py-3 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-635" data-mp="2ota1dt4sb67">
            <div className="flex items-center gap-2" data-id="element-636" data-mp="6qn5n7ogyozi">
              <span className="w-2 h-2 bg-[#4F46E5]" aria-hidden="true" data-id="element-637" data-mp="booopslwaoaz" />
              <span className="font-sans text-xs font-semibold tracking-wide text-[#121212]/70 uppercase" data-id="element-638" data-mp="bndilm4z94gp">
                {isFR ? 'Assistant ATNA' : 'ATNA Assistant'}
              </span>
            </div>
            <span className="font-sans text-[11px] text-[#5B6478]" data-id="element-639" data-mp="3f3cak430a2fv">
              {isFR ? 'Démonstration · valeurs illustratives' : 'Demonstration · illustrative values'}
            </span>
          </div>

          <div className="p-6 md:p-8" data-id="element-640" data-mp="3cimq8k4e3vne">
            {!activeQuestion ? (/* IDLE — answer-first entry: questions + input */
          <div data-id="element-641" data-mp="32m1ktxogkij1">
                <p className="font-sans text-base text-[#5B6478] mb-6" data-id="element-642" data-mp="32lhze6zlnc09">
                  {isFR ? 'Posez une question sur BCITI et ATNA.' : 'Ask a question about BCITI and ATNA.'}
                </p>
                <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-4" data-id="element-643" data-mp="2tny033larpsa">
                  {isFR ? 'Questions suggérées' : 'Suggested questions'}
                </div>
                <div className="flex flex-col gap-3 mb-8" data-id="element-644" data-mp="2ex8kzq4ptqj5">
                  {vertical.questions.map(q => <button key={q} onClick={() => pick(q)} className="group flex items-center justify-between text-left border border-[#E4E6EC] bg-white rounded-lg px-5 py-4 font-sans text-base text-[#121212] hover:border-[#4F46E5] transition-colors" data-id="element-645" data-mp="2avdjolmsbd5d">
                      {questionLabel(q, isFR)}
                      <ArrowUpRightIcon className="w-4 h-4 text-[#4F46E5] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-3" data-id="element-646" data-mp="3m2sbgc2oqe" />
                    </button>)}
                </div>
                {/* Decorative input — guides to suggested questions (demo) */}
                <div className="flex items-center gap-3 border border-[#E4E6EC] px-4 py-3" data-id="element-647" data-mp="3r9vwksj3rhv5">
                  <input value={typed} onChange={e => setTyped(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') setShowHint(true);
              }} placeholder={isFR ? 'Demander à ATNA…' : 'Ask ATNA…'} className="flex-1 bg-transparent outline-none font-sans text-base text-[#121212] placeholder:text-[#5B6478]" data-id="element-648" data-mp="3r7j6fs36y170" />
                  <button onClick={() => setShowHint(true)} aria-label={isFR ? 'Envoyer' : 'Send'} className="shrink-0 w-9 h-9 bg-[#4F46E5] text-white flex items-center justify-center hover:bg-[#4F46E5]/90 transition-colors" data-id="element-649" data-mp="1qmaqrudu6cmb">
                    <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-650" data-mp="1um340ke6d6y0" />
                  </button>
                </div>
                {showHint && <p className="mt-2 font-sans text-[11px] text-[#5B6478]" data-id="element-651" data-mp="24dz420ztn74v">
                    {isFR ? 'Cette démonstration ne répond qu’aux questions suggérées ci-dessus.' : 'This demonstration answers the suggested questions above.'}
                  </p>}
              </div>) : resolved && (/* ANSWER-FIRST */
          <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-652" data-mp="jynp1efqaeno">
                  <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-2" data-id="element-653" data-mp="2j3xawls4g7rb">
                    {isFR ? 'Votre question' : 'You asked'}
                  </div>
                  <p className="font-serif text-lg text-[#121212] mb-8" data-id="element-654" data-mp="2syyxb9v0ivq7">
                    {questionLabel(activeQuestion as string, isFR)}
                  </p>

                  <div className="font-sans text-xs font-semibold tracking-wider text-[#4F46E5] uppercase mb-3" data-id="element-655" data-mp="31zx39xp0hdbf">
                    {isFR ? 'Réponse' : 'Answer'}
                  </div>
                  <p className="font-serif text-xl md:text-2xl text-[#121212] leading-snug mb-8" data-id="element-656" data-mp="3bq8abt3zs2mj">
                    {resolved.recommendation}
                  </p>

                  <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-4" data-id="element-657" data-mp="3grp65a7mcyw9">
                    {isFR ? 'Points clés' : 'Key points'}
                  </div>
                  <div className="space-y-2.5 mb-8" data-id="element-658" data-mp="3po6j8izjn6un">
                    {resolved.results.slice(0, 4).map((r, i) => <div key={i} className="flex items-start gap-3" data-id="element-659" data-mp="1a1u783fyeg7t" data-mpi={i}>
                        <span className="w-1.5 h-1.5 bg-[#4F46E5] shrink-0 mt-2" aria-hidden="true" data-id="element-660" data-mp="1a3orrgnbc6bd" />
                        <span className="font-sans text-base text-[#121212]" data-id="element-661" data-mp="154c814rqnfd0">{r}</span>
                      </div>)}
                  </div>

                  {/* Compact verification score — optional detail */}
                  <VerificationScore label={resolved.label} isFR={isFR} expanded={expanded} onToggle={() => {
              setExpanded(v => !v);
              if (!expanded) trackEvent('atna_verification_expanded', {
                page,
                vertical: vertical.tag
              });
            }} data-id="element-662" data-mp="1srk2ssrjj60s" />
                  <AnimatePresence initial={false} data-id="element-663" data-mp="2bqob1zb9cvre">
                    {expanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="overflow-hidden" data-id="element-664" data-mp="1k34pyeh82ss5">
                        <div className="pt-4 space-y-4" data-id="element-665" data-mp="1f279c6bv26sr">
                          <VerificationLabel label={resolved.label} isFR={isFR} data-id="element-666" data-mp="22tmcg1bgos52" />
                          <WhyThis label={resolved.label} isFR={isFR} data-id="element-667" data-mp="27th7mh29s3vx" />
                          <InformActNote isFR={isFR} data-id="element-668" data-mp="27ql7cm2y934b" />
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* CTA */}
                  <div className="flex flex-wrap items-center gap-4 pt-8 mt-8 border-t border-[#E4E6EC]" data-id="element-669" data-mp="ie0yh1ocii61">
                    <Link to={`/request-demo?source=atna&vertical=${vertical.tag}`} onClick={() => trackEvent('atna_demo_clicked', {
                page,
                vertical: vertical.tag,
                recommended_product: resolved.modules.join(',')
              })} className="inline-flex items-center gap-2 px-6 py-3 bg-[#4F46E5] text-white text-sm font-medium hover:bg-[#4F46E5]/90 transition-colors" data-id="element-670" data-mp="nd416fguscau">
                      {isFR ? 'Demander une démo personnalisée' : 'Request a personalized demo'}
                      <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-671" data-mp="3na2eyrspxc8h" />
                    </Link>
                    <button onClick={reset} className="font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-672" data-mp="14oxncod9dkr">
                      {isFR ? 'Poser une autre question' : 'Ask another question'}
                    </button>
                  </div>
                </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}